// The Modify tools in the plan — Offset, Trim / Extend, Fillet / Chamfer: their
// picks, the offset's typed distance and preview, and the result written into the
// drawing (`applyRingEdits`: an object takes its first ring, a further ring is a new
// piece, and everything numbered by their vertices follows). The geometry is
// modifyOps.ts's, pure (docs/claude/tools.md, *Modify tools*).
import {
  type Polygon,
  type Vec2,
  type Wall,
} from '../../shared/types';
import { moveVertices } from '../anchors';
import {
  invalidate,
  newId,
  pushHistory,
  state,
} from './core';
import { drawingTab } from './drawingTab';
import {
  fillet,
  offsetToward,
  type Ring,
  type RingEdit,
  ringOf,
  ringVertex,
  storedPoints,
  trimRing,
} from '../modifyOps';
import { parseMeasure } from './panelRows';
import {
  endOneShot,
  freshCopies,
  LINE_DEFAULT_COLOR,
  LINE_DEFAULT_HEIGHT,
  LINE_DEFAULT_THICKNESS,
  objectAt,
  syncDimensions,
} from '../main';

/** This module's state that main.ts reads and writes too. */
export const modifyTools = {
  // OFFSET's typed distance (mm), remembered between uses as in AutoCAD; null is
  // "Through": the copy passes through the click.
  offsetDist: null as number | null,
  modifyState: null as ModifyState | null,
};

// ---- Modify tools (offset / trim / fillet): shared transient pick state ----
// `offset`: `first` is the picked source object; move the cursor to the side to
//   offset toward, click to drop a parallel copy (then back to select).
// `trim`: `first` is the cutting-edge object; click a segment to cut it there.
// `fillet`: `first` is the first picked edge object; pick a second to round/chamfer.
// The geometry is modifyOps.ts's, pure; this writes it into the drawing.
interface ModifyState { first: { kind: 'poly' | 'wall'; id: string; at: Vec2 } | null; preview: Vec2[] | null }

/** What a modify tool picked: the polygon or the wall / line, and the ring it is
 *  drawn on (`ringOf`: one closure rule for every tool). */
function modifyTarget(kind: 'poly' | 'wall', id: string): { shape: Polygon | Wall; ring: Ring; isWall: boolean } | null {
  const isWall = kind === 'wall';
  const shape = isWall ? (state.drawing.walls || []).find((x) => x.id === id) : (state.drawing.polygons || []).find((x) => x.id === id);
  return shape ? { shape, ring: ringOf(shape.points, isWall), isWall } : null;
}

/** The offset copy's outline as the preview draws it (a closed one closed). */
export function offsetPreview(cursor: Vec2): Vec2[] | null {
  const t = modifyTools.modifyState?.first ? modifyTarget(modifyTools.modifyState.first.kind, modifyTools.modifyState.first.id) : null;
  const out = t ? offsetToward(t.ring, cursor, modifyTools.offsetDist) : null;
  return out ? storedPoints(out, true) : null;
}

/** OFFSET's typed value is its distance; it stays set for the next use. */
export function typeOffsetDistance(raw: string): void {
  const v = parseMeasure(raw);
  if (v == null || v <= 0) return;
  modifyTools.offsetDist = Math.round(v * 1000) / 1000;
  if (modifyTools.modifyState?.first && state.hoverWorld) modifyTools.modifyState.preview = offsetPreview(state.hoverWorld);
}

/** Pick a polygon or wall/line under the cursor for a modify tool. */
export function pickModifyObject(world: Vec2): { kind: 'poly' | 'wall'; id: string } | null {
  const w = objectAt('wall', world);
  if (w) return { kind: 'wall', id: w.id };
  const p = objectAt('polygon', world);
  if (p) return { kind: 'poly', id: p.id };
  return null;
}

/** Drop a parallel copy of the offset source through the cursor point. Returns
 *  false if the source or offset can't be resolved. The copy keeps the source's
 *  look and its locks, not its pinned lengths or positions, which the offset
 *  changed. */
export function commitOffset(cursor: Vec2): boolean {
  if (!modifyTools.modifyState?.first) return false;
  const t = modifyTarget(modifyTools.modifyState.first.kind, modifyTools.modifyState.first.id);
  const out = t ? offsetToward(t.ring, cursor, modifyTools.offsetDist) : null;
  if (!t || !out) return false;
  pushHistory();
  const cp = structuredClone(t.shape);
  cp.id = newId(t.isWall ? 'wall' : 'poly');
  cp.points = storedPoints(out, t.isWall);
  if (t.isWall) delete (cp as Wall).segLengths;
  else {
    const poly = cp as Polygon;
    delete poly.holes;
    delete poly.edgeLengths;
    poly.relations = poly.relations?.filter((r) => r.type !== 'lockX' && r.type !== 'lockY' && r.type !== 'distancePP');
    if (!poly.relations?.length) delete poly.relations;
  }
  if (t.isWall) (state.drawing.walls ??= []).push(cp as Wall);
  else (state.drawing.polygons ??= []).push(cp as Polygon);
  freshCopies.add(cp.id); // on its source's layer, not the current one
  invalidate();
  return true;
}

/** Write modify results into the drawing: each object takes its first ring, a
 *  further one becomes a new piece (a copy of it, on its layer), and everything
 *  numbered by their vertices follows (anchors.ts `moveVertices`). */
function applyRingEdits(edits: Array<{ shape: Polygon | Wall; isWall: boolean; edit: RingEdit }>): void {
  moveVertices(state.drawing, edits.map((e) => e.shape.id), () => {
    const old = edits.map((e) => e.shape.points);
    const ids = edits.map(({ shape, isWall, edit }) => edit.rings.map((r, i) => {
      if (i === 0) { shape.points = storedPoints(r, isWall); return shape.id; }
      const piece = structuredClone(shape);
      piece.id = newId(isWall ? 'wall' : 'poly');
      piece.points = storedPoints(r, isWall);
      if (isWall) (state.drawing.walls ??= []).push(piece as Wall);
      else (state.drawing.polygons ??= []).push(piece as Polygon);
      freshCopies.add(piece.id);
      return piece.id;
    }));
    return (id, v) => {
      const k = edits.findIndex((e) => e.shape.id === id);
      const to = k < 0 ? null : edits[k].edit.map(ringVertex(old[k], edits[k].isWall, v));
      return to ? { id: ids[k][to.ring], vertex: to.vertex } : null;
    };
  });
}

/** TRIM / EXTEND the clicked part of `target` at the cutting object
 *  (modifyOps.ts `trimRing`). */
function commitTrim(cutting: { kind: 'poly' | 'wall'; id: string }, target: { kind: 'poly' | 'wall'; id: string; at: Vec2 }): boolean {
  if (cutting.kind === target.kind && cutting.id === target.id) return false;
  const cut = modifyTarget(cutting.kind, cutting.id), t = modifyTarget(target.kind, target.id);
  const edit = cut && t ? trimRing(t.ring, target.at, cut.ring, !t.isWall) : null;
  if (!t || !edit) return false;
  pushHistory();
  applyRingEdits([{ ...t, edit }]);
  syncDimensions();
  invalidate();
  return true;
}

/** FILLET (a true arc) or CHAMFER (a straight bevel) the corner between the
 *  clicked sides of two objects (modifyOps.ts `fillet`); the arc or the bevel
 *  between two objects is drawn in the first one's colour and stroke. */
function commitFillet(first: { kind: 'poly' | 'wall'; id: string; at: Vec2 }, second: { kind: 'poly' | 'wall'; id: string; at: Vec2 }): boolean {
  const a = modifyTarget(first.kind, first.id), b = modifyTarget(second.kind, second.id);
  if (!a || !b) return false;
  const same = first.kind === second.kind && first.id === second.id;
  const res = fillet({ ring: a.ring, at: first.at, area: !a.isWall }, { ring: b.ring, at: second.at, area: !b.isWall }, same, drawingTab.filletRadius, drawingTab.filletMode === 'chamfer');
  if (!res) return false;
  const wsrc = a.isWall ? (a.shape as Wall) : null;
  const strokeColor = wsrc?.fill ?? LINE_DEFAULT_COLOR;
  const strokeW = wsrc?.thickness ?? LINE_DEFAULT_THICKNESS;
  pushHistory();
  applyRingEdits([{ ...a, edit: res.first }, ...(res.second ? [{ ...b, edit: res.second }] : [])]);
  if (res.arc) (state.drawing.arcs ??= []).push({ id: newId('arc'), ...res.arc, stroke: strokeColor, strokeWidth: strokeW });
  if (res.bevel) {
    (state.drawing.walls ??= []).push({
      id: newId('wall'), points: res.bevel, style: 'line',
      thickness: strokeW, align: 'center',
      fill: strokeColor, fillOpacity: 1, stroke: wsrc?.stroke, strokeOpacity: 1, height: LINE_DEFAULT_HEIGHT,
    } as Wall);
  }
  syncDimensions();
  invalidate();
  return true;
}

/** Two-click flow shared by the trim and fillet tools. */
export function handleModifyClick(tool: 'trim' | 'fillet', world: Vec2): void {
  const hit = pickModifyObject(world);
  if (!modifyTools.modifyState?.first) {
    if (hit) { modifyTools.modifyState = { first: { ...hit, at: world }, preview: null }; invalidate(); }
    return;
  }
  if (!hit) return;
  const first = modifyTools.modifyState.first;
  const done = tool === 'trim'
    ? commitTrim({ kind: first.kind, id: first.id }, { ...hit, at: world })
    : commitFillet(first, { ...hit, at: world });
  if (tool === 'fillet') { modifyTools.modifyState = null; invalidate(); } // fillet consumes both picks
  if (done) endOneShot();
}
